import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Message, MessageService } from 'primeng/api';

/**
 * Toast service class
 * This class provides methods to add single, multiple alerts as a toast
 */
@Injectable({
  providedIn: 'root',
})
export class ToastService {
  constructor(
    private messageService: MessageService,
    private translateService: TranslateService
  ) {}

  info(detail: string, sticky?: boolean, key?: string): void {
    this.translateService.get('SigvLibrary.Info').subscribe({
      next: (str) => {
        this.addSingle('info', str, detail, sticky, key);
      },
    });
  }

  success(detail: string, sticky?: boolean, key?: string): void {
    this.translateService.get('SigvLibrary.Success').subscribe({
      next: (str) => {
        this.addSingle('success', str, detail, sticky, key);
      },
    });
  }

  warn(detail: string, sticky?: boolean, key?: string): void {
    this.translateService.get('SigvLibrary.Warn').subscribe({
      next: (str) => {
        this.addSingle('warn', str, detail, sticky, key);
      },
    });
  }

  error(detail: string, sticky?: boolean, key?: string): void {
    this.translateService.get('SigvLibrary.Error').subscribe({
      next: (str) => {
        this.addSingle('error', str, detail, sticky, key);
      },
    });
  }

  /**
   * add single toast message
   * @param severity Severity level of the message, valid values are "success", "info", "warn" and "error"
   * @param summary Summary text of the message.
   * @param detail Detail text of the message.
   * @param sticky Whether the message should be automatically closed based on life property or kept visible.
   * @param key Key of the message in case message is targeted to a specific toast component.
   * @param life Number of time in milliseconds to wait before closing the message. Default 3000.
   */
  addSingle(
    severity: string,
    summary: string,
    detail: string,
    sticky?: boolean,
    key?: string,
    life = 5000
  ): void {
    this.messageService.add({
      severity,
      summary,
      detail,
      sticky,
      key,
      life,
    });
  }

  /**
   * add multiple toast messages
   * @param messages array of message type {severity:'success', summary:'Service Message', detail:'Via MessageService'}
   */
  addMultiple(messages: Message[]): void {
    this.messageService.addAll(messages);
  }

  /**
   * add single multi-line toast message
   * @param severity Severity level of the message, valid values are "success", "info", "warn" and "error"
   * @param summary Summary text of the message.
   * @param details String array of details.
   * @param sticky Whether the message should be automatically closed based on life property or kept visible.
   * @param key Key of the message in case message is targeted to a specific toast component.
   * @param life Number of time in milliseconds to wait before closing the message. Default 3000.
   */
  addSingleMultiLine(
    severity: string,
    summary: string,
    details: string[],
    sticky?: boolean,
    key?: string,
    life = 5000
    ): void {
    const detail = details.length > 0 ? details.splice(0, 6).join('\n') + '\n...' : '';
    this.messageService.add({
      severity,
      summary,
      detail,
      sticky,
      key,
      life
    });
  }

  /**
   * clear all toast messages
   */
  clear(): void {
    this.messageService.clear();
  }
}
